import type { Download } from "./client";
import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { PageRequest } from "./types";

/**
 * The billing module: accounts, postings, payments, statements and the club's billing settings.
 * Every method names its operation id (`Out<"billing_account_summary">`), so a renamed route fails to compile.
 */
export function billingEndpoints({ http, get, post, patch, put, del, paged }: Helpers) {
  return {
    // ---- the accounts list and one account
    /** Households with their balance. `balanceFilter`: owing, credit, zero or over_limit. */
    listAccounts: (page: PageRequest & { q?: string; lifecycle?: string; status?: string; balanceFilter?: string } = {}) =>
      get<Out<"billing_list_accounts_with_balances">>(
        "/billing/households",
        paged(page, { q: page.q, lifecycle: page.lifecycle, status: page.status, balance_filter: page.balanceFilter }),
      ),
    accountSummary: (householdId: string, signal?: AbortSignal) =>
      get<Out<"billing_account_summary">>(`/billing/households/${householdId}/summary`, { signal }),
    /** Newest first; each row carries the balance after it. */
    listTransactions: (
      householdId: string,
      page: PageRequest & { dateFrom?: string; dateTo?: string; kind?: string; departmentId?: string; memberId?: string; q?: string } = {},
    ) =>
      get<Out<"billing_list_household_transactions">>(
        `/billing/households/${householdId}/transactions`,
        paged(page, { date_from: page.dateFrom, date_to: page.dateTo, kind: page.kind, department_id: page.departmentId, member_id: page.memberId, q: page.q }),
      ),
    transactionDetail: (id: string, signal?: AbortSignal) => get<Out<"billing_transaction_detail">>(`/billing/transactions/${id}`, { signal }),
    openItems: (householdId: string, page: PageRequest = {}) =>
      get<Out<"billing_open_items">>(`/billing/households/${householdId}/open-items`, paged(page)),
    /** Posts the household's imported balance as its opening entry. */
    bringBalanceForward: (householdId: string) => post<Out<"billing_bring_balance_forward">>(`/billing/households/${householdId}/opening-balance`),

    // ---- posting and corrections
    /** A charge, or with `dry_run` the same work without writing anything. */
    postCharge: (input: In<"billing_post_a_charge">) => post<Out<"billing_post_a_charge">>("/billing/postings", input),
    recordPayment: (input: In<"billing_record_a_payment">) => post<Out<"billing_record_a_payment">>("/billing/payments", input),
    applyPayment: (transactionId: string, input: In<"billing_apply_a_payment">) =>
      post<Out<"billing_apply_a_payment">>(`/billing/payments/${transactionId}/allocations`, input),
    unapplyPayment: (transactionId: string, input: In<"billing_unapply_a_payment">) =>
      post<Out<"billing_unapply_a_payment">>(`/billing/payments/${transactionId}/unallocate`, input),
    reversePayment: (transactionId: string, input: In<"billing_reverse_a_payment">) =>
      post<Out<"billing_reverse_a_payment">>(`/billing/payments/${transactionId}/reverse`, input),
    /** Everything but a payment (billing.adjust). */
    reverseTransaction: (transactionId: string, input: In<"billing_reverse_a_transaction">) =>
      post<Out<"billing_reverse_a_transaction">>(`/billing/transactions/${transactionId}/reverse`, input),
    postAdjustment: (input: In<"billing_post_a_credit_or_adjustment">) => post<Out<"billing_post_a_credit_or_adjustment">>("/billing/adjustments", input),
    postWriteOff: (input: In<"billing_post_a_write_off">) => post<Out<"billing_post_a_write_off">>("/billing/write-offs", input),

    // ---- statements
    listStatements: (page: PageRequest & { householdId?: string; q?: string; runId?: string } = {}) =>
      get<Out<"billing_list_statements">>("/billing/statements", paged(page, { household_id: page.householdId, q: page.q, run_id: page.runId })),
    readStatement: (id: string, signal?: AbortSignal) => get<Out<"billing_read_statement">>(`/billing/statements/${id}`, { signal }),
    /** The statement's PDF as a file to hand to the browser. */
    downloadStatement: (id: string) =>
      http.request<Download>("GET", `/billing/statements/${id}/pdf`, { responseType: "blob" }),

    /** A household's statements with the history of what happened to each (needs billing.read). */
    householdCorrespondence: (householdId: string, page: PageRequest = {}) =>
      get<Out<"billing_household_correspondence">>(`/billing/households/${householdId}/correspondence`, paged(page)),
    /** `email` sends the PDF now; `printed` and `mailed` record that someone did (needs billing.close). */
    addStatementDelivery: (statementId: string, input: In<"billing_add_statement_delivery">) =>
      post<Out<"billing_add_statement_delivery">>(`/billing/statements/${statementId}/deliveries`, input),
    /** The email a resend would send, in the club's wording, before sending. */
    statementEmailPreview: (statementId: string, signal?: AbortSignal) =>
      get<Out<"billing_preview_statement_email">>(`/billing/statements/${statementId}/email-preview`, { signal }),

    // ---- settings
    readSettings: (signal?: AbortSignal) => get<Out<"billing_read_settings">>("/billing/settings", { signal }),
    updateSettings: (input: In<"billing_update_settings">) => patch<Out<"billing_update_settings">>("/billing/settings", input),

    listDepartments: (signal?: AbortSignal) => get<Out<"billing_list_departments">>("/billing/departments", { signal }),
    createDepartment: (input: In<"billing_create_department">) => post<Out<"billing_create_department">>("/billing/departments", input),
    updateDepartment: (id: string, input: In<"billing_update_department">) =>
      patch<Out<"billing_update_department">>(`/billing/departments/${id}`, input),

    listItems: (page: PageRequest & { q?: string; departmentId?: string; active?: boolean } = {}) =>
      get<Out<"billing_list_items">>("/billing/items", paged(page, { q: page.q, department_id: page.departmentId, active: page.active })),
    createItem: (input: In<"billing_create_item">) => post<Out<"billing_create_item">>("/billing/items", input),
    updateItem: (id: string, input: In<"billing_update_item">) => patch<Out<"billing_update_item">>(`/billing/items/${id}`, input),

    listTaxes: (signal?: AbortSignal) => get<Out<"billing_list_taxes">>("/billing/taxes", { signal }),
    createTax: (input: In<"billing_create_tax">) => post<Out<"billing_create_tax">>("/billing/taxes", input),
    updateTax: (id: string, input: In<"billing_update_tax">) => patch<Out<"billing_update_tax">>(`/billing/taxes/${id}`, input),

    /** The members module's minimum plans, with or without terms. */
    listMinimumPlans: (signal?: AbortSignal) => get<Out<"billing_list_minimum_plans">>("/billing/minimum-plans", { signal }),
    setMinimumTerms: (code: string, input: In<"billing_set_minimum_terms">) =>
      put<Out<"billing_set_minimum_terms">>(`/billing/minimum-plans/${encodeURIComponent(code)}`, input),
    clearMinimumTerms: (code: string) => del<Out<"billing_clear_minimum_terms">>(`/billing/minimum-plans/${encodeURIComponent(code)}/terms`),

    listArrangements: (signal?: AbortSignal) => get<Out<"billing_list_arrangements">>("/billing/arrangements", { signal }),
    setArrangementTerms: (code: string, input: In<"billing_set_arrangement_terms">) =>
      put<Out<"billing_set_arrangement_terms">>(`/billing/arrangements/${encodeURIComponent(code)}`, input),
    clearArrangementTerms: (code: string) => del<Out<"billing_clear_arrangement_terms">>(`/billing/arrangements/${encodeURIComponent(code)}/terms`),

    listFeeRates: (page: PageRequest & { categoryCode?: string; activityCode?: string } = {}) =>
      get<Out<"billing_list_fee_rates">>("/billing/fee-rates", paged(page, { category_code: page.categoryCode, activity_code: page.activityCode })),
    createFeeRate: (input: In<"billing_create_fee_rate">) => post<Out<"billing_create_fee_rate">>("/billing/fee-rates", input),
    updateFeeRate: (id: string, input: In<"billing_update_fee_rate">) => patch<Out<"billing_update_fee_rate">>(`/billing/fee-rates/${id}`, input),
    deleteFeeRate: (id: string) => del<Out<"billing_delete_fee_rate">>(`/billing/fee-rates/${id}`),

    listStatementMessages: (signal?: AbortSignal) => get<Out<"billing_list_messages">>("/billing/statement-messages", { signal }),
    createStatementMessage: (input: In<"billing_create_message">) => post<Out<"billing_create_message">>("/billing/statement-messages", input),
    updateStatementMessage: (id: string, input: In<"billing_update_message">) =>
      patch<Out<"billing_update_message">>(`/billing/statement-messages/${id}`, input),
    deleteStatementMessage: (id: string) => del<Out<"billing_delete_message">>(`/billing/statement-messages/${id}`),

    listReasons: (signal?: AbortSignal) => get<Out<"billing_list_reasons">>("/billing/adjustment-reasons", { signal }),
    createReason: (input: In<"billing_create_reason">) => post<Out<"billing_create_reason">>("/billing/adjustment-reasons", input),
    updateReason: (id: string, input: In<"billing_update_reason">) => patch<Out<"billing_update_reason">>(`/billing/adjustment-reasons/${id}`, input),

    /** The chart of accounts. */
    listChart: (page: PageRequest & { q?: string; type?: string; active?: boolean } = {}) =>
      get<Out<"billing_list_accounts">>("/billing/accounts", paged(page, { q: page.q, type: page.type, active: page.active })),
    createChartAccount: (input: In<"billing_create_account">) => post<Out<"billing_create_account">>("/billing/accounts", input),
    updateChartAccount: (id: string, input: In<"billing_update_account">) => patch<Out<"billing_update_account">>(`/billing/accounts/${id}`, input),
  };
}
